import React from 'react';
import { Route, Routes, Navigate, Outlet } from 'react-router-dom';
import SignUp from './layouts/Auth/SignUp/SignUp';
import AgentSignUp from './layouts/Auth/AgentSignUp/AgentSignUp';
import LogIn from './layouts/Auth/LogIn/LogIn';
import OTP from './layouts/Auth/OTP/OTP';
import Auth from './layouts/Auth/Auth';
import Dashboard from './layouts/Main/Dashboard/Dashboard';
import AgentDashboard from './layouts/Main/AgentDashboard/Dashboard';
import AgentUploadFile from './layouts/Main/AgentUploadFile/AgentUploadFile';
import SelectFund from './layouts/Main/AgentUploadFile/SelectFund';
import GenerateLink from './layouts/Main/AgentUploadFile/GenerateLink';
import Reset from './layouts/Auth/Reset/Reset';
import Employees from './layouts/Main/Employees/Employees';
import File from './layouts/Main/File/File';
import Clients from './layouts/Main/Clients/Clients';
import Client from './layouts/Main/Clients/Client/Client';
import Reports from './layouts/Main/Reports/Reports';
import Agents from './layouts/Main/Agents/Agents';
import AgentsList from './layouts/Main/Agents/List/List';
import AgentsPayments from './layouts/Main/Agents/Payments/Payments';
import AgentsFunds from './layouts/Main/Agents/Funds/Funds';
import Agent from './layouts/Main/Agents/List/Agent/Agent';
import ProtectedRoute from './protectedRoute';
import PublicRoute from './publicRoute';
import { UserRole } from './utils/types/types';
import { useAuth } from './utils/context/useAuthContext';
import Unauthorized from './layouts/Auth/Unauthorized';
import CustomPayment from './layouts/Main/CustomPayment/CustomPayment';
import Assignments from './layouts/Main/Assignments/Assignments';
import Fees from './layouts/Main/Fees/Fees';

// Компонент для обработки дашборда с редиректом админов
const DashboardHandler: React.FC = () => {
  const { user } = useAuth();
  
  // Если пользователь админ, перенаправляем на clients
  if (user?.role === UserRole.ADMIN) {
    return <Navigate to="/clients" replace />;
  }
  
  // Для всех остальных показываем обычный дашборд
  return <Dashboard />;
};

const AppRouter: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute>
            <Auth />
          </PublicRoute>
        }
      >
        <Route index element={<SignUp />} />
        <Route path="signup" element={<SignUp />} />
        <Route path="signup-agent" element={<AgentSignUp />} />
        <Route path="reset" element={<Reset />} />
        <Route path="login" element={<LogIn />} />
        <Route path="otp" element={<OTP />} />
      </Route>

      <Route
        path="dashboard"
        element={
          <ProtectedRoute>
            <DashboardHandler />
          </ProtectedRoute>
        }
      />
      <Route
        path="agent-dashboard"
        element={
          <ProtectedRoute allowedRoles={[UserRole.AGENT]}>
            <AgentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="upload-file"
        element={
          <ProtectedRoute allowedRoles={[UserRole.AGENT]}>
            <AgentUploadFile />
          </ProtectedRoute>
        }
      >
        <Route path="select-fund" element={<SelectFund />} />
        <Route path="generate-link" element={<GenerateLink />} />
        <Route index element={<Navigate to="select-fund" replace />} />
      </Route>
      <Route
        path="employees"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN, UserRole.CLIENT]}>
            <Employees />
          </ProtectedRoute>
        }
      />
      <Route
        path="clients"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <Clients />
          </ProtectedRoute>
        }
      />
      <Route
        path="custom-payment"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <CustomPayment />
          </ProtectedRoute>
        }
      />
      <Route
        path="clients/:uuid"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <Client />
          </ProtectedRoute>
        }
      />
      <Route
        path="all-files"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route path="agents" element={<Agents />}>
        <Route element={<ProtectedRoute allowedRoles={[UserRole.ADMIN]} />}>
          <Route index element={<AgentsList />} />
          <Route path="list" element={<AgentsList />} />
          <Route path="funds" element={<AgentsFunds />} />
          <Route path="payments" element={<AgentsPayments />} />
        </Route>
      </Route>
      <Route
        path="agent/:uuid"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <Agent />
          </ProtectedRoute>
        }
      />
      <Route
        path="file-overview"
        element={
          <ProtectedRoute>
            <File />
          </ProtectedRoute>
        }
      />
      <Route
        path="assignments"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <Assignments />
          </ProtectedRoute>
        }
      />
      <Route
        path="fees"
        element={
          <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
            <Fees />
          </ProtectedRoute>
        }
      />
      <Route path="unauthorized" element={<Unauthorized />} />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRouter;
